"use client";

import { motion } from "framer-motion";
import { Send, Loader2 } from "lucide-react";
import { useState, useEffect, useCallback } from "react";
import { cn } from "@/lib/utils";
import { handleFormSubmit } from "@/app/actions/contactActions";
import { useRouter } from "next/navigation";

interface CompanySize {
  value: string;
  label: string;
}

const COMPANY_SIZES: CompanySize[] = [
  { value: "1-10", label: "1-10 Mitarbeiter" },
  { value: "11-100", label: "11-100 Mitarbeiter" },
  { value: "101-1000", label: "101-1000 Mitarbeiter" },
  { value: "1000+", label: "1000+ Mitarbeiter" }
];

type SearchType = "job" | "employee" | "other" | null;

const motionVariants = {
  container: {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  },
  item: {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 }
  }
};

const inputClasses = cn(
  "w-full px-4 py-3 rounded-xl",
  "bg-gradient-floral border border-white/40",
  "shadow-neumorphic-inset",
  "focus:shadow-neumorphic-inset-lg",
  "outline-none transition-all duration-300",
  "text-foreground placeholder:text-muted-foreground"
);

const radioClasses = cn(
  "form-radio",
  "text-primary focus:ring-ring"
);

interface ContactFormProps {
  preselect?: string | string[] | null;
  type?: string | null;
  role?: string | null;
  sourceUrl?: string | null;
}

export function ContactForm({ preselect, type, role, sourceUrl }: ContactFormProps) {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  
  const getInitialSearchType = useCallback((): SearchType => {
    const firstPreselect = Array.isArray(preselect) ? preselect[0] : preselect;
    if (firstPreselect === 'candidate') return 'job';
    if (firstPreselect === 'company') return 'employee';
    return (firstPreselect as SearchType) || null;
  }, [preselect]);
  
  const [searchType, setSearchType] = useState<SearchType>(getInitialSearchType());
  const [otherSearch, setOtherSearch] = useState("");

  useEffect(() => {
    setSearchType(getInitialSearchType());
  }, [preselect, getInitialSearchType]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    
    try {
      const formData = new FormData(e.currentTarget);
      
      // Get current URL as referrer
      const referrerUrl = window.location.href;
      formData.append('referrer', referrerUrl);
      
      // Skip Matomo visitor ID for now to avoid TypeScript errors
      // TODO: Fix Matomo visitor ID tracking once TypeScript definitions are updated
      formData.append('id', 'not-available');
      
      // Convert searchType to anliegen as expected by server
      const currentSearchType = formData.get('searchType') as string;
      formData.append('anliegen', currentSearchType);
      
      // Add other search details if needed
      if (currentSearchType === 'other' && otherSearch) {
        formData.append('anliegen_detail', otherSearch);
      }
      
      // Call the server action
      const result = await handleFormSubmit(formData);
      
      if (result.success) {
        setSuccess(true);
        // Show success message for a moment, then redirect to About Us page
        setTimeout(() => {
          router.push('/about-us');
        }, 2000);
      } else {
        setError(result.error || 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setError('Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.');
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <section className="py-24 bg-white font-gilroy min-h-screen">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-lg mx-auto p-8 rounded-xl shadow-neumorphic bg-gradient-floral"
          >
            <h2 className="text-h2 mb-4">Vielen Dank!</h2>
            <p className="text-body mb-4">Ihre Anfrage wurde erfolgreich übermittelt. Wir werden uns in Kürze bei Ihnen melden.</p>
            <p className="text-body-sm">Sie werden in Kürze weitergeleitet...</p>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-24 bg-white font-gilroy min-h-screen">
      <div className="container mx-auto px-4">
        <motion.div
          variants={motionVariants.item}
          initial="hidden"
          animate="show"
          className="text-center mb-16"
        >
          <h2 className="text-h2 mb-2">
            Kontakt aufnehmen & Early Access
          </h2>
          <p className="text-body mb-4 max-w-2xl mx-auto">
            Werden Sie Teil unseres exklusiven Early-Access-Programms und erleben Sie als einer der Ersten die Zukunft des Recruitments.
          </p>
          <div className="w-24 h-1 bg-thistle mx-auto rounded-full" />
        </motion.div>

        {error && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="max-w-2xl mx-auto mb-6 p-4 rounded-xl bg-red-50 border border-red-200 text-red-700"
          >
            {error}
          </motion.div>
        )}

        <motion.form
          variants={motionVariants.container}
          initial="hidden"
          animate="show"
          onSubmit={handleSubmit}
          className="max-w-2xl mx-auto space-y-6"
        >
          {/* Hidden fields for tracking */}
          {type && <input type="hidden" name="type" value={type} />}
          {role && <input type="hidden" name="role" value={role} />}
          {sourceUrl && <input type="hidden" name="sourceUrl" value={sourceUrl} />}

          <motion.div variants={motionVariants.item}>
            <label className="block text-body-sm font-medium text-foreground mb-2">
              Vollständiger Name
            </label>
            <input
              type="text"
              name="name"
              required
              className={inputClasses}
              placeholder="Max Mustermann"
            />
          </motion.div>

          <motion.div variants={motionVariants.item}>
            <label className="block text-body-sm font-medium text-foreground mb-2">
              E-Mail
            </label>
            <input
              type="email"
              name="email"
              required
              className={inputClasses}
              placeholder="max@beispiel.de"
            />
          </motion.div>

          <motion.div variants={motionVariants.item} className="space-y-3">
            <label className="block text-body-sm font-medium text-foreground mb-2">
              Was ist Ihr Anliegen?
            </label>
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="searchType"
                  value="job"
                  checked={searchType === "job"}
                  onChange={() => setSearchType("job")}
                  className={radioClasses}
                />
                <span className="text-body text-foreground">Ich suche einen Job / Ich interessiere mich für die Umynd Services</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="searchType"
                  value="employee"
                  checked={searchType === "employee"}
                  onChange={() => setSearchType("employee")}
                  className={radioClasses}
                />
                <span className="text-body text-foreground">Ich suche einen Mitarbeiter / Ich interessiere mich für die Umynd Revolution im Recruiting</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="searchType"
                  value="other"
                  checked={searchType === "other"}
                  onChange={() => setSearchType("other")}
                  className={radioClasses}
                />
                <span className="text-body text-foreground">Ich möchte mehr Informationen / Ich suche etwas Anderes</span>
              </label>
            </div>
            {searchType === "other" && (
              <input
                type="text"
                name="message"
                value={otherSearch}
                onChange={(e) => setOtherSearch(e.target.value)}
                placeholder="Bitte beschreiben Sie Ihr Anliegen..."
                className={inputClasses}
              />
            )}
          </motion.div>

          {searchType === "employee" && (
            <>
              <motion.div variants={motionVariants.item}>
                <label className="block text-body-sm font-medium text-foreground mb-2">
                  Unternehmen
                </label>
                <input
                  type="text"
                  name="companyName"
                  required
                  className={inputClasses}
                  placeholder="Firmenname"
                />
              </motion.div>

              <motion.div variants={motionVariants.item}>
                <label className="block text-body-sm font-medium text-foreground mb-2">
                  Unternehmensgröße
                </label>
                <select
                  name="companySize"
                  required
                  className={cn(
                    inputClasses,
                    "appearance-none cursor-pointer"
                  )}
                >
                  <option value="">Unternehmensgröße auswählen</option>
                  {COMPANY_SIZES.map(size => (
                    <option key={size.value} value={size.value}>
                      {size.label}
                    </option>
                  ))}
                </select>
              </motion.div>
            </>
          )}

          <motion.div variants={motionVariants.item}>
            <button
              type="submit"
              disabled={isLoading}
              className={cn(
                "w-full px-6 py-3 rounded-xl",
                "bg-gradient-floral",
                "transition-all duration-300",
                "text-body font-medium",
                "flex items-center justify-center gap-2",
                "border border-white/40",
                "shadow-neumorphic",
                "hover:shadow-neumorphic-lg",
                "active:shadow-neumorphic-inset",
                "disabled:opacity-70 disabled:cursor-not-allowed"
              )}
            >
              {isLoading ? (
                <Loader2 className="w-5 h-5 animate-spin text-foreground" />
              ) : (
                <>
                  <Send className="w-5 h-5 text-foreground" />
                  <span className="text-foreground">Anfrage senden</span>
                </>
              )}
            </button>
          </motion.div>
        </motion.form>
      </div>
    </section>
  );
} 